import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  KeyIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi } from '@/lib/api';
import toast from 'react-hot-toast';

export default function ProjectSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showApiKey, setShowApiKey] = useState(false);
  const [showDatabaseUrl, setShowDatabaseUrl] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: projectResponse, isLoading: projectLoading } = useQuery(
    ['project', id],
    () => projectsApi.get(id as string),
    { enabled: isAuthenticated && !!id }
  );

  const regenerateKeyMutation = useMutation(
    () => projectsApi.regenerateApiKey(id as string),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['project', id]);
        toast.success('API key regenerated successfully');
      },
      onError: (error: unknown) => {
        const axiosErr = error as { response?: { data?: { message?: string } } };
        toast.error(axiosErr?.response?.data?.message || 'Failed to regenerate API key');
      },
    }
  );

  const deleteMutation = useMutation(
    () => projectsApi.delete(id as string),
    {
      onSuccess: () => {
        toast.success('Project deleted successfully');
        navigate('/projects');
      },
      onError: (error: unknown) => {
        const axiosErr = error as { response?: { data?: { message?: string } } };
        toast.error(axiosErr?.response?.data?.message || 'Failed to delete project');
      },
    }
  );

  if (authLoading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  if (!project) {
    return <div className="text-center text-gray-500">Project not found</div>;
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const parseDatabaseUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return {
        username: urlObj.username || '',
        password: urlObj.password || '',
        hostname: urlObj.hostname || '',
        port: urlObj.port || '5432',
        database: urlObj.pathname.slice(1) || '',
      };
    } catch {
      return { username: '', password: '', hostname: '', port: '', database: '' };
    }
  };

  const dbCredentials = parseDatabaseUrl(project.database_url);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connection details and project configuration for {project.name}.
        </p>
      </div>

      {/* Connection Details */}
      <div className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Database URL</label>
              <div className="mt-1 flex">
                <input
                  type={showDatabaseUrl ? 'text' : 'password'}
                  value={project.database_url}
                  readOnly
                  className="form-input flex-1 font-mono text-xs"
                />
                <button
                  onClick={() => setShowDatabaseUrl(!showDatabaseUrl)}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {showDatabaseUrl ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => copyToClipboard(project.database_url, 'Database URL')}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Connection Parameters</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Username</label>
                  <div className="mt-1 flex">
                    <input type="text" value={dbCredentials.username} readOnly className="form-input flex-1 font-mono text-xs" />
                    <button onClick={() => copyToClipboard(dbCredentials.username, 'Username')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Password</label>
                  <div className="mt-1 flex">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={dbCredentials.password}
                      readOnly
                      className="form-input flex-1 font-mono text-xs"
                    />
                    <button onClick={() => setShowPassword(!showPassword)} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                    <button onClick={() => copyToClipboard(dbCredentials.password, 'Password')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Hostname</label>
                  <div className="mt-1 flex">
                    <input type="text" value={dbCredentials.hostname} readOnly className="form-input flex-1 font-mono text-xs" />
                    <button onClick={() => copyToClipboard(dbCredentials.hostname, 'Hostname')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Port</label>
                    <div className="mt-1 flex">
                      <input type="text" value={dbCredentials.port} readOnly className="form-input flex-1 font-mono text-xs" />
                      <button onClick={() => copyToClipboard(dbCredentials.port, 'Port')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Database</label>
                    <div className="mt-1 flex">
                      <input type="text" value={dbCredentials.database} readOnly className="form-input flex-1 font-mono text-xs" />
                      <button onClick={() => copyToClipboard(dbCredentials.database, 'Database')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Key */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">API Key</h2>
          <div className="flex">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={project.api_key}
              readOnly
              className="form-input flex-1 font-mono text-xs"
            />
            <button onClick={() => setShowApiKey(!showApiKey)} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
              {showApiKey ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
            <button onClick={() => copyToClipboard(project.api_key, 'API Key')} className="ml-2 p-2 text-gray-400 hover:text-gray-600">
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4">
            <button
              onClick={() => regenerateKeyMutation.mutate()}
              disabled={regenerateKeyMutation.isLoading}
              className="btn-secondary disabled:opacity-50"
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              {regenerateKeyMutation.isLoading ? 'Regenerating...' : 'Regenerate API Key'}
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="card border border-red-200">
          <h2 className="text-lg font-medium text-red-600 mb-2">Danger Zone</h2>
          <p className="text-sm text-gray-500 mb-4">
            Permanently delete this project and all its data. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn-danger"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete Project
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Project</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete &quot;{project.name}&quot;? This action cannot be undone and will permanently delete the database and all its data.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button onClick={() => setShowDeleteModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    deleteMutation.mutate();
                    setShowDeleteModal(false);
                  }}
                  disabled={deleteMutation.isLoading}
                  className="btn-danger disabled:opacity-50"
                >
                  {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
